import { useCallback, useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import api from "../lib/api";
import { usePolling } from "../lib/usePolling";
import { useAuth } from "../lib/auth";
import { DispatchMap } from "../components/DispatchMap";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { Truck, Plus, Route, Copy, RefreshCw, X, Trash2, Check, MapPin, Package, PackageCheck, Clock, CircleDot } from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
const STATUS_META = {
  pending: { label: "Pending", color: "#8A6D1B", bg: "#F7F1DE", icon: Clock },
  arrived: { label: "Arrived", color: "#2B5FB3", bg: "#EAF0FB", icon: CircleDot },
  done: { label: "Done", color: "#2F6F4E", bg: "#E3F0E7", icon: PackageCheck },
};

export default function DispatchPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [cfg, setCfg] = useState(null);
  const [stops, setStops] = useState([]);
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showCfg, setShowCfg] = useState(false);
  const [optimizing, setOptimizing] = useState(false);

  const loadCfg = useCallback(async () => {
    try { const { data } = await api.get("/dispatch/config"); setCfg(data); } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("dispatch config load failed", e?.message || e); }
  }, []);

  const loadLive = useCallback(async () => {
    try {
      const { data } = await api.get(`/dispatch/live?date=${date}`);
      setStops(data.stops || []);
      setDriver(data.driver || null);
    } catch { toast.error("Could not load dispatch"); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { loadCfg(); }, [loadCfg]);
  useEffect(() => { setLoading(true); loadLive(); }, [loadLive]);
  usePolling(loadLive, 12000, !showAdd && !showCfg);

  const suggested = useMemo(() => stops.filter((s) => s.review), [stops]);
  const active = useMemo(() => stops.filter((s) => !s.review), [stops]);
  const doneCount = active.filter((s) => s.status === "done").length;

  const link = cfg ? `${BASE}${cfg.path}` : "";

  async function optimize() {
    setOptimizing(true);
    try {
      const { data } = await api.post("/dispatch/optimize", { date });
      if (data.warning) toast.warning(data.warning);
      else toast.success(`Route optimized — ${data.count} stops ordered`);
      loadLive();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not optimize"); }
    finally { setOptimizing(false); }
  }

  async function setStatus(s, status) {
    try { await api.put(`/dispatch/stops/${s.id}`, { status }); loadLive(); }
    catch { toast.error("Update failed"); }
  }
  async function approve(s) {
    try { await api.post(`/dispatch/stops/${s.id}/approve`); toast.success("Added to route"); loadLive(); }
    catch { toast.error("Failed"); }
  }
  async function remove(s) {
    if (!window.confirm("Remove this stop?")) return;
    try { await api.delete(`/dispatch/stops/${s.id}`); loadLive(); }
    catch { toast.error("Delete failed"); }
  }

  return (
    <div data-testid="dispatch-panel">
      {/* Driver link card */}
      <div className="bg-white border border-[#E5E3DB] rounded-lg p-4 mb-5 flex flex-wrap items-center gap-5">
        <div className="flex items-center gap-3">
          {link && <QRCodeSVG value={link} size={84} data-testid="dispatch-qr" />}
          <div>
            <div className="text-xs uppercase tracking-wide text-[#68665E]">EV Driver</div>
            <div className="font-headings text-lg font-bold text-[#1A1A18] flex items-center gap-2" data-testid="dispatch-driver-name">
              <Truck className="w-4 h-4 text-[#C84B31]" /> <span>{cfg?.driver_name || "—"}</span>
              {cfg?.vehicle_number && <span className="font-mono text-sm text-[#68665E]">· {cfg.vehicle_number}</span>}
            </div>
            <div className="text-[11px] text-[#68665E] mt-0.5">Permanent driver link — scan the QR or share the URL</div>
          </div>
        </div>
        <div className="flex-1 min-w-[200px]" />
        <div className="flex items-center gap-2">
          <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }} className="btn-secondary" data-testid="dispatch-copy-link"><Copy className="w-4 h-4" /> Copy link</button>
          {isAdmin && <button onClick={() => setShowCfg(true)} className="btn-secondary" data-testid="dispatch-config-btn"><RefreshCw className="w-4 h-4" /> Driver settings</button>}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-xs text-[#68665E]">Day</label>
          <input type="date" className="input-field !py-1.5 text-sm !w-auto" value={date} onChange={(e) => setDate(e.target.value)} data-testid="dispatch-date" />
          <span className="text-xs text-[#68665E]">{doneCount}/{active.length} done</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={optimize} disabled={optimizing || active.length < 2} className="btn-secondary" data-testid="dispatch-optimize-btn"><Route className="w-4 h-4" /> {optimizing ? "Optimizing…" : "Optimize route"}</button>
          <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="dispatch-add-btn"><Plus className="w-4 h-4" /> Add stop</button>
        </div>
      </div>

      {suggested.length > 0 && (
        <div className="border border-[#E7C97A] bg-[#FBF6E7] rounded-lg p-3 mb-4" data-testid="dispatch-suggested">
          <div className="text-xs font-semibold text-[#8A6D1B] uppercase tracking-wide mb-2">AI-suggested from email — review before dispatch</div>
          <div className="space-y-2">
            {suggested.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-2 bg-white border border-[#E5E3DB] rounded p-2" data-testid={`dispatch-suggested-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#1A1A18] truncate">{s.stop_type === "pickup" ? "Pickup" : "Deliver"} · {s.company || "—"}</div>
                  <div className="text-[11px] text-[#68665E] truncate">{s.address}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => approve(s)} className="btn-secondary !py-1 !px-2 text-xs" data-testid={`dispatch-approve-${s.id}`}><Check className="w-3.5 h-3.5" /> Add</button>
                  <button onClick={() => remove(s)} className="text-[#8A2526] hover:opacity-70 p-1" data-testid={`dispatch-reject-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Stop list */}
        <div>
          {loading ? <div className="text-sm text-[#68665E]">Loading…</div> :
            active.length === 0 ? <div className="text-sm text-[#68665E] border border-dashed border-[#E5E3DB] rounded p-6 text-center" data-testid="dispatch-empty">No stops for this day yet. Add one or wait for L&T email suggestions.</div> :
              <div className="space-y-2" data-testid="dispatch-stops">
                {active.map((s, i) => {
                  const m = STATUS_META[s.status] || STATUS_META.pending;
                  const Icon = s.stop_type === "pickup" ? Package : PackageCheck;
                  return (
                    <div key={s.id} className="bg-white border border-[#E5E3DB] rounded-lg p-3" data-testid={`dispatch-stop-${s.id}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-[#1A1A18] text-white flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{i + 1}</div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-[#1A1A18] flex items-center gap-1"><Icon className="w-3.5 h-3.5 text-[#C84B31]" />{s.stop_type === "pickup" ? "Pickup" : "Delivery"}</span>
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ color: m.color, background: m.bg }} data-testid={`dispatch-status-${s.id}`}>{m.label}</span>
                            {s.lr_number && <span className="font-mono text-[10px] text-[#2B5FB3]">{s.lr_number}</span>}
                            {s.lat == null && <span className="text-[10px] text-[#8A2526]">no map location</span>}
                          </div>
                          <div className="text-sm text-[#1A1A18] mt-0.5">{s.company || "—"}</div>
                          <div className="text-[11px] text-[#68665E] flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0" />{s.address || "—"}</div>
                          {(s.contact_name || s.contact_phone) && <div className="text-[11px] text-[#68665E]">{s.contact_name} {s.contact_phone && `· ${s.contact_phone}`}</div>}
                          {s.notes && <div className="text-[11px] text-[#68665E] italic">{s.notes}</div>}
                        </div>
                        <button onClick={() => remove(s)} className="text-[#68665E] hover:text-[#8A2526] p-1 shrink-0" data-testid={`dispatch-del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                      <div className="flex items-center gap-1.5 mt-2 pl-10">
                        {["pending", "arrived", "done"].map((st) => (
                          <button key={st} onClick={() => setStatus(s, st)}
                            className={`text-[11px] px-2 py-1 rounded border transition-colors ${s.status === st ? "border-[#1A1A18] bg-[#1A1A18] text-white" : "border-[#E5E3DB] text-[#68665E] hover:border-[#C84B31]"}`}
                            data-testid={`dispatch-set-${st}-${s.id}`}>{STATUS_META[st].label}</button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>}
        </div>

        {/* Live map */}
        <div>
          <DispatchMap stops={active} driver={driver} height={420} />
          <div className="text-[11px] text-[#68665E] mt-2 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#1A73E8] inline-block" /> Driver</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#8A6D1B] inline-block" /> Pickup</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#C84B31] inline-block" /> Delivery</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#2F6F4E] inline-block" /> Done</span>
            {driver?.updated_at && <span>· last ping {new Date(driver.updated_at).toLocaleTimeString()}</span>}
          </div>
        </div>
      </div>

      {showAdd && <AddStopModal date={date} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); loadLive(); }} />}
      {showCfg && <ConfigModal cfg={cfg} onClose={() => setShowCfg(false)} onSaved={() => { setShowCfg(false); loadCfg(); }} />}
    </div>
  );
}

function AddStopModal({ date, onClose, onSaved }) {
  const [f, setF] = useState({ stop_type: "delivery", company: "", address: "", contact_name: "", contact_phone: "", notes: "", lr_number: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  async function save() {
    if (!f.address.trim() && !f.company.trim()) { toast.error("Add a company or address"); return; }
    setSaving(true);
    try { await api.post("/dispatch/stops", { ...f, date }); toast.success("Stop added"); onSaved(); }
    catch { toast.error("Could not add stop"); }
    finally { setSaving(false); }
  }
  return (
    <Modal title="Add dispatch stop" onClose={onClose} testId="dispatch-add-modal">
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className="text-xs text-[#68665E]">Type</span>
          <select className="input-field !py-1.5 text-sm mt-1" value={f.stop_type} onChange={(e) => set("stop_type", e.target.value)} data-testid="add-stop-type">
            <option value="pickup">Pickup</option><option value="delivery">Delivery</option>
          </select></label>
        <Field label="LR # (optional)" value={f.lr_number} onChange={(e) => set("lr_number", e.target.value)} testId="add-stop-lr" />
        <Field label="Company / Party" value={f.company} onChange={(e) => set("company", e.target.value)} testId="add-stop-company" />
        <Field label="Contact phone" value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} testId="add-stop-phone" />
        <div className="col-span-2">
          <label className="block"><span className="text-xs text-[#68665E]">Address</span>
            <AddressAutocomplete value={f.address} onChange={(v) => set("address", v)} className="input-field !py-1.5 text-sm mt-1" placeholder="Start typing an address…" testId="add-stop-address" />
          </label>
        </div>
        <div className="col-span-2"><Field label="Notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} testId="add-stop-notes" /></div>
      </div>
      <button onClick={save} disabled={saving} className="btn-primary w-full mt-4" data-testid="add-stop-save">{saving ? "Saving…" : "Add stop"}</button>
    </Modal>
  );
}

function ConfigModal({ cfg, onClose, onSaved }) {
  const [name, setName] = useState(cfg?.driver_name || "");
  const [veh, setVeh] = useState(cfg?.vehicle_number || "");
  async function save() {
    try { await api.put("/dispatch/config", { driver_name: name, vehicle_number: veh }); toast.success("Saved"); onSaved(); }
    catch { toast.error("Could not save"); }
  }
  async function regen() {
    if (!window.confirm("Generate a new link? The old link will stop working.")) return;
    try { await api.post("/dispatch/config/regenerate"); toast.success("New link generated"); onSaved(); }
    catch { toast.error("Failed"); }
  }
  return (
    <Modal title="Driver settings" onClose={onClose} testId="dispatch-config-modal">
      <div className="space-y-3">
        <Field label="Driver name" value={name} onChange={(e) => setName(e.target.value)} testId="cfg-driver-name" />
        <Field label="Vehicle number" value={veh} onChange={(e) => setVeh(e.target.value)} testId="cfg-vehicle" />
        <button onClick={save} className="btn-primary w-full" data-testid="cfg-save">Save</button>
        <button onClick={regen} className="btn-ghost text-[#8A2526] text-sm w-full justify-center" data-testid="cfg-regen"><RefreshCw className="w-4 h-4" /> Regenerate driver link</button>
      </div>
    </Modal>
  );
}

function Field({ label, testId, ...p }) {
  return (<label className="block"><span className="text-xs text-[#68665E]">{label}</span><input className="input-field !py-1.5 text-sm mt-1" data-testid={testId} {...p} /></label>);
}

function Modal({ title, children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-headings font-bold text-[#1A1A18]">{title}</h3>
          <button onClick={onClose} className="text-[#68665E] hover:text-[#C84B31]"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
